'use client';

import { Image } from '@packages/shared/src/ui/Image/Image';
import { Link } from '@packages/modules/src/Link/Link';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Card } from '@packages/shared/src/ui/Card/Card';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@packages/shared/src/ui/HoverCard/HoverCard';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { Flag } from '@packages/shared/src/ui/Flag/Flag';
import { type VariantProps } from 'class-variance-authority';
import type { flagVariants } from '@packages/shared/src/ui/Flag/Flag.variants';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import type {
  CheckoutAdditionalFlagType,
  CheckoutAvailabilityState,
} from '@packages/gql/generated/shopping/graphql';
import type { GlycerinCartProduct } from '@packages/tracking/src/types/types';
import { PowerSupplyLocalized } from '@packages/modules/src/PowerSupplyLocalized/PowerSupplyLocalized.client';
import { AvailabilityInfo } from '@packages/modules/src/AvailabilityInfo/AvailabilityInfo.client';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import type { GTMEventGlycerinProductClick } from '@packages/tracking/src/types/events';
import { EfficiencyFlags } from '@packages/modules/src/EfficiencyFlags/Order/EfficiencyFlags.client';
import { LineItemPrice } from './LineItemPrice.client';
import type { LineItemDisplayProps } from './LineItemTypes';

const messages = defineMessages({
  lineItemConfigurationSERIALNUMBER: {
    id: 'LineItem.Configuration.SerialNumber',
    defaultMessage: 'Bedienelement / Seriennummer: {serialNumber}',
  },
  lineItemConfigurationWIDTH: {
    id: 'LineItem.Configuration.Width',
    defaultMessage: 'Breite: {width} cm',
  },
  lineItemConfigurationLENGTH: {
    id: 'LineItem.Configuration.Length',
    defaultMessage: 'Länge: {length} cm',
  },
  lineItemConfigurationENGRAVING: {
    id: 'LineItem.Configuration.Engraving',
    defaultMessage: 'Ihr Gravurtext: {engraving}',
  },
  sustainedFlag: {
    id: 'LineItem.SustainedFlag',
    defaultMessage: 'Nachhaltig',
  },
});

export const getFlagVariant = (
  flagType: CheckoutAdditionalFlagType | undefined | null,
): VariantProps<typeof flagVariants>['variant'] => {
  const typeVariantMap: Record<
    CheckoutAdditionalFlagType,
    NonNullable<VariantProps<typeof flagVariants>['variant']>
  > = {
    CUSTOM: 'contrast',
    NEUTRAL: 'neutral',
    SALE: 'sale',
    SECONDARY: 'secondary',
    SUSTAINABILITY: 'sustainability',
  };

  return flagType ? typeVariantMap[flagType] : undefined;
};

export const LineItemContent = ({
  name,
  sku,
  akl,
  imageUrl,
  variation,
  sustained,
  marketingFlag,
  additionalFlags,
  configuration,
  powerEfficiency,
  tireEfficiency,
  powerSupply,
  price,
  singlePrice,
  isSoldOut,
  availability,
  quantity,
  actionsSlot,
  aplusSlot,
  glycerinProduct,
}: LineItemDisplayProps & { glycerinProduct: GlycerinCartProduct }) => {
  const { formatPrice } = useFormatPrice();
  const { language, localizeUrl } = useI18n();
  const { formatMessage } = useIntl();
  const productLinkParams = new URLSearchParams({ sku }).toString();
  const dispatchGTMEvent = useTracking();

  const handleProductClick = () => {
    dispatchGTMEvent<GTMEventGlycerinProductClick>({
      event: 'ProductClick',
      ProductClickData: {
        category: 'Cart',
        productId: akl!,
        articleId: glycerinProduct.articleId,
        skuId: sku,
      },
    });
  };

  return (
    <Card className="relative mb-1.5 w-full overflow-visible pt-1.5 last:mb-0 md:mb-2 last:md:mb-0">
      <div className="flex flex-nowrap gap-1.5 overflow-visible px-1.5 pb-1.5">
        {/* Image Section */}
        <HoverCard>
          <HoverCardTrigger asChild>
            <Link
              className="relative flex size-[80px] shrink-0 flex-col items-center justify-center md:size-[120px]"
              href={localizeUrl(`/p/${akl}?${productLinkParams}`, language)}
              onClick={() => handleProductClick()}
            >
              <Image
                className="object-contain"
                preload
                unoptimized
                alt={name}
                src={imageUrl}
                fill
              />
              {sustained && (
                <Flag
                  text={formatMessage(messages.sustainedFlag)}
                  variant="sustainability"
                  className="absolute top-2.5 left-0 overflow-hidden"
                />
              )}
            </Link>
          </HoverCardTrigger>
          <HoverCardContent side="right" className="bg-color-background-base hidden p-2 md:block">
            <div className="relative h-[240px] w-[240px]">
              <Image
                className="object-contain"
                preload
                unoptimized
                alt={name}
                src={imageUrl}
                fill
              />
            </div>
          </HoverCardContent>
        </HoverCard>
        {/* Product Details Section */}
        <div className="flex grow flex-col gap-1.5">
          <div className="flex flex-col gap-2 md:flex-row md:flex-nowrap">
            <div className="grow">
              {marketingFlag && <Flag text={marketingFlag.text} variant="contrast" />}
              {additionalFlags && additionalFlags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {additionalFlags.map((flag) =>
                    flag.src ? (
                      <div
                        className="relative h-3 max-h-3 w-[5.625rem] max-w-[5.625rem]"
                        key={flag.text}
                      >
                        <Image src={`${flag.src}.png`} alt={flag.text} fill />
                      </div>
                    ) : (
                      <Flag
                        key={flag.text}
                        text={flag.text}
                        variant={getFlagVariant(flag.type as CheckoutAdditionalFlagType | null)}
                      />
                    ),
                  )}
                </div>
              )}
              <div className="mr-5 pb-2 md:mr-1">
                <h4 className="mb-0 leading-tight font-semibold md:mb-1">{name}</h4>
                <p className="text-size-body-sm">{variation}</p>
                {configuration &&
                  Object.entries(configuration).map(
                    ([key, value]) =>
                      key !== '__typename' &&
                      value && (
                        <p className="text-color-text-secondary text-size-body-sm" key={key}>
                          {formatMessage(
                            messages[
                              `lineItemConfiguration${key.toUpperCase()}` as keyof typeof messages
                            ],
                            { [key]: value },
                          )}
                        </p>
                      ),
                  )}
              </div>
              <p className="text-size-body-sm inline-block pb-1">
                <FormattedMessage
                  key="LineItem.Quantity-text"
                  id="LineItem.Quantity"
                  defaultMessage="Anzahl: {quantity}"
                  values={{ quantity }}
                />
              </p>
              {singlePrice && singlePrice.amount && (
                <div className="text-size-body-sm ml-1 inline-block">
                  <FormattedMessage
                    key="LineItem.SinglePrice-text"
                    id="LineItem.SinglePrice"
                    defaultMessage=" (à {singlePrice})"
                    values={{
                      singlePrice: formatPrice(
                        singlePrice.amount,
                        singlePrice.currency satisfies string,
                      ),
                    }}
                  />
                </div>
              )}
              {availability.limitedStock && (
                <div>
                  <Flag
                    text={formatMessage({
                      id: 'LineItem.LimitedStock',
                      defaultMessage: 'Fast ausverkauft',
                    })}
                    variant="contrast"
                    className="bg-color-text-dark"
                  />
                </div>
              )}
              <AvailabilityInfo
                availabilityMessage={availability.message}
                availabilityState={availability.state as CheckoutAvailabilityState}
              />
              <div className="flex flex-col pt-1">
                <EfficiencyFlags
                  powerFlags={powerEfficiency}
                  tireFlags={tireEfficiency}
                  tireDataSheet={tireEfficiency?.[0]?.dataSheet}
                />
              </div>
              {powerSupply && (
                <PowerSupplyLocalized
                  minPower={powerSupply.minPower}
                  maxPower={powerSupply.maxPower}
                  chargerType={powerSupply.chargerType as 'Normal' | 'PowerDelivery'}
                  powerSupplyIncluded={
                    powerSupply.powerSupplyIncluded as 'included' | 'notIncluded'
                  }
                  showPowerSupplyMessage
                />
              )}
            </div>
            {!isSoldOut && <LineItemPrice price={price} />}
          </div>
        </div>
      </div>
      {/* APlus Section */}
      {!!aplusSlot && (
        <div className="mt-0.5">
          <Divider />
          {aplusSlot}
        </div>
      )}
      {/* Actions Section */}
      {actionsSlot}
    </Card>
  );
};
